import React, { useRef } from 'react';
import { COLORS, FONTS } from '../../constants';
import styled from 'styled-components';
import { motion, useInView } from 'motion/react';
import { ANIMATION_CONFIG } from '../../constants/animationConfig';

interface HeadProps extends React.HTMLProps<HTMLHeadingElement> {
  text?: string | null;
  minHeight?: number;
  fontFamily?: string;
  color?: string;
  opacity?: number;
  size?: number;
  weight?: number;
  lineHeight?: number;
  margin?: string;
  wrap?: string;
  maxWidth?: number;
  isAnimated?: boolean;
  yOffset?: number;
  duration?: number;
  delay?: number;
}

export const Text: React.FC<HeadProps> = ({
  text,
  minHeight = 0,
  fontFamily = FONTS.SUBTITLE,
  color = COLORS.SECTION_TEXT,
  opacity = 1,
  size = 20,
  weight = 400,
  lineHeight = 28,
  margin = `0`,
  wrap = 'wrap',
  maxWidth,
  isAnimated = false,
  yOffset = ANIMATION_CONFIG.text.yOffset,
  duration = ANIMATION_CONFIG.text.duration,
  delay = ANIMATION_CONFIG.text.delay,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: ANIMATION_CONFIG.text.amount,
  });

  return (
    <SmallText
      $minHeight={minHeight}
      $fontFamily={fontFamily}
      $color={color}
      $opacity={opacity}
      $size={size}
      $weight={weight}
      $lineHeight={lineHeight}
      $margin={margin}
      $wrap={wrap}
      $maxWidth={maxWidth}
      ref={ref}
      {...(isAnimated
        ? {
            initial: { opacity: 0, y: yOffset },
            animate: isInView
              ? { opacity: 1, y: 0 }
              : { opacity: 0, y: yOffset },
            transition: {
              duration: duration,
              delay: delay,
              type: 'spring',
            },
          }
        : {})}
    >
      {text}
    </SmallText>
  );
};

const SmallText = styled(motion.h4)<{
  $minHeight?: number;
  $fontFamily?: string;
  $color?: string;
  $opacity?: number;
  $size?: number;
  $weight?: number;
  $lineHeight?: number;
  $margin?: string;
  $wrap?: string;
  $maxWidth?: number;
}>`
  min-height: ${props => `${props.$minHeight}px`};
  font-family: ${props => props.$fontFamily};
  color: ${props => props.$color};
  opacity: ${props => props.$opacity};
  font-size: ${props => `${props.$size}px`};
  font-weight: ${props => props.$weight};
  line-height: ${props => `${props.$lineHeight}px`};
  margin: ${props => props.$margin};
  padding: 0;
  text-wrap: ${props => props.$wrap};
  ${props => props.$maxWidth && `max-width: ${props.$maxWidth}px`};
`;
